import { createFileRoute } from "@tanstack/react-router";
import * as z from "zod";

import { BIRDIE_FULLSCREEN_ACTION } from "@/features/shared/global-birdie-chat/_hooks/use-birdie-chat-mode";
import {
  MaskRedirect,
  maskSearchFragment,
} from "@/features/shared/route-masking";

/**
 * Deep-link / bookmark shim for the full-screen Birdie overlay. Full screen is
 * a masked overlay (not a standalone page), so hitting
 * `/admin/global-birdie-chat` directly just redirects into the admin shell with
 * the `birdie-fullscreen` action set, masked back to this clean URL. The shared
 * `GlobalBirdieChat` host mounted in the admin layout renders the overlay.
 */
export const Route = createFileRoute("/admin/global-birdie-chat")({
  validateSearch: z.object({
    // Share links append the conversation to open — must survive the
    // MaskRedirect spread or a shared link lands on a fresh chat.
    "conversation-id": z.string().optional(),
    // A Birdie v4 conversation's share link (features/shared/birdie-v4-chat share-link.ts).
    "shared-session": z.string().optional(),
    ...maskSearchFragment,
  }),
  component: RouteComponent,
});

function RouteComponent() {
  // Forward the shared chat over the mask contract's `prefill` key — a bare
  // search param would be stripped by the landing route's (`/admin/customers`)
  // validateSearch, which doesn't declare it.
  const {
    "conversation-id": conversationId,
    "shared-session": sharedSessionId,
  } = Route.useSearch();
  const prefill =
    conversationId || sharedSessionId
      ? {
          ...(conversationId ? { conversationId } : {}),
          ...(sharedSessionId ? { sharedSessionId } : {}),
        }
      : undefined;
  const maskSearch =
    conversationId || sharedSessionId
      ? {
          ...(conversationId ? { "conversation-id": conversationId } : {}),
          ...(sharedSessionId ? { "shared-session": sharedSessionId } : {}),
        }
      : undefined;
  return (
    <MaskRedirect
      to="/admin/customers"
      action={BIRDIE_FULLSCREEN_ACTION}
      mask="/admin/global-birdie-chat"
      prefill={prefill}
      maskSearch={maskSearch}
    />
  );
}
